import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {getPublicFile} from 'helpers/envHelper'
import styled from 'styled-components'
import WrapElement from 'pages/HealthCenter/ReportV1/Elements/WrapElement'
import useHealthCenterBrainFunctionMappingStyles from 'pages/HealthCenter/ReportV1/BrainFunctionMapping/Style'
import TableFiguresByPart from 'pages/HealthCenter/ReportV1/BrainFunctionMapping/Elements/tableFiguresByPart'
import useLangEn from 'hooks/useLangEn'
import {useAppSelector} from 'hooks'
import {
  getCurrentLanguageReport,
  selectLanguageReport,
} from 'features/languageReport/languageReportSlice'

export const PageWrap = styled.div`
  background: White;
`

export const PageContent = styled.div`
  width: 1160px;
`

export const CardContent = styled.div`
  font-size: 14px;
`

export const FlexBox = styled.div`
  display: flex;
  padding: 0 15px;
  justify-content: space-between;
`
const getHealthReportReportImage = getPublicFile('images/health_center_report')

interface BrainFunctionMappingProps {
  Frontal_LH: number
  Temporal_LH: number
  agingRoiScore: Record<string, any>
}

const getNumber = (value: number | string) => {
  if (!Number.isNaN(Number(value))) return Math.floor(Number(value))
  return 0
}

const scoreToString = (score: number) => {
  if (Math.floor(score) < 30) {
    return 'IScoreBrainMapping_Lowering'
  }
  if (Math.floor(score) > 70) {
    return 'IScoreBrainMapping_Exaltation'
  }

  return 'IScoreBrainMapping_Balance'
}

const scoreToClass = (score: number) => {
  if (Math.floor(score) < 30) {
    return 'LoweringActive'
  }
  if (Math.floor(score) > 70) {
    return 'ExaltationActive'
  }

  return 'Balanced'
}

const scoreSign = (score: number | string) => {
  const parsedScore =
    typeof score === 'string' ? Number.parseFloat(score) : score

  if (parsedScore < 30) {
    return getHealthReportReportImage('down.svg')
  }
  if (parsedScore > 70) {
    return getHealthReportReportImage('up.svg')
  }

  return getHealthReportReportImage('arrow.svg')
}

const Label = ({score}: {score: number | string}) => {
  const {t} = useTranslation()
  const classes = useHealthCenterBrainFunctionMappingStyles()
  const languageReport = useAppSelector((state) =>
    selectLanguageReport(state.languageReport),
  )
  const currentLanguagueReport = getCurrentLanguageReport(languageReport, 'es')
  const getText = () => {
    if (Math.floor(Number(score)) < 30) {
      return currentLanguagueReport?.health?.brain_mapping.status.low
    }
    if (Math.floor(Number(score)) > 70) {
      return currentLanguagueReport?.health?.brain_mapping.status.high
    }
    return currentLanguagueReport?.health?.brain_mapping.status.normal
  }
  const className = scoreToClass(getNumber(score))
  return (
    <div className={`${classes.labelScore} ${className}`}>
      {getText()}
      <img src={scoreSign(score)} alt='icon' />
    </div>
  )
}

const Score = ({score}: {score: number}) => {
  const {t} = useTranslation()
  const classes = useHealthCenterBrainFunctionMappingStyles()

  return (
    <div className={classes.bottom16}>
      <div className={classes.scoreNumber}>
        {score}
        {t('IPoints')}
      </div>
      <Label score={score} />
    </div>
  )
}

function TablePrintT1({
  Frontal_LH,
  Temporal_LH,
  agingRoiScore,
}: BrainFunctionMappingProps) {
  const {t} = useTranslation()
  const isEn = useLangEn()
  const classes = useHealthCenterBrainFunctionMappingStyles()
  const languageReport = useAppSelector((state) =>
    selectLanguageReport(state.languageReport),
  )
  const currentLanguagueReport = getCurrentLanguageReport(languageReport, 'es')
  const headTable = [
    currentLanguagueReport?.health?.brain_mapping.advanced.section.temporal
      .score_table.headers[0] || '',
    currentLanguagueReport?.health?.brain_mapping.advanced.section.frontal
      .score_table.headers[1] || '',
    currentLanguagueReport?.health?.brain_mapping.advanced.section.frontal
      .score_table.headers[2] || '',
    currentLanguagueReport?.health?.brain_mapping.advanced.section.frontal
      .score_table.headers[3] || '',
    currentLanguagueReport?.health?.brain_mapping.advanced.section.frontal
      .score_table.headers[4] || '',
  ]

  const bodyTable3 = useMemo(
    () => [
      {
        data: [
          {
            header: [
              {
                // key: 'T1.',
                // content: 'Temporal Pole',
                // note: t('ITemporalPoleT1'),
                key: currentLanguagueReport?.health?.brain_mapping.advanced
                  .section.temporal.score_table.rows[0].rois[0].code,
                content:
                  currentLanguagueReport?.health?.brain_mapping.advanced.section
                    .temporal.score_table.rows[0].rois[0].name,
                note: currentLanguagueReport?.health?.brain_mapping.advanced
                  .section.temporal.score_table.rows[0].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainT1.png'),
          },
          currentLanguagueReport?.health?.brain_mapping.advanced.section
            .temporal.score_table.rows[0].functions,
          currentLanguagueReport?.health?.brain_mapping.advanced.section
            .temporal.score_table.rows[0].symptoms,
          [getNumber(agingRoiScore?.Temporal_Pole_LH)],
          [getNumber(agingRoiScore?.Temporal_Pole_RH)],
        ],
        background: 'White',
      },
      {
        data: [
          {
            header: [
              {
                // key: 'T2.',
                // content: 'Superior Temporal',
                // note: t('ISuperiorT2'),
                key: currentLanguagueReport?.health?.brain_mapping.advanced
                  .section.temporal.score_table.rows[1].rois[0].code,
                content:
                  currentLanguagueReport?.health?.brain_mapping.advanced.section
                    .temporal.score_table.rows[1].rois[0].name,
                note: currentLanguagueReport?.health?.brain_mapping.advanced
                  .section.temporal.score_table.rows[1].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainT2.png'),
          },
          currentLanguagueReport?.health?.brain_mapping.advanced.section
            .temporal.score_table.rows[1].functions,
          currentLanguagueReport?.health?.brain_mapping.advanced.section
            .temporal.score_table.rows[1].symptoms,
          [getNumber(agingRoiScore?.Superior_Temporal_LH)],
          [getNumber(agingRoiScore?.Superior_Temporal_RH)],
        ],
        background: 'White',
      },
      {
        data: [
          {
            header: [
              {
                // key: 'T3.',
                // content: 'Middle Temporal',
                // note: t('IMiddleT3'),
                key: currentLanguagueReport?.health?.brain_mapping.advanced
                  .section.temporal.score_table.rows[2].rois[0].code,
                content:
                  currentLanguagueReport?.health?.brain_mapping.advanced.section
                    .temporal.score_table.rows[2].rois[0].name,
                note: currentLanguagueReport?.health?.brain_mapping.advanced
                  .section.temporal.score_table.rows[2].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainT3.png'),
          },
          currentLanguagueReport?.health?.brain_mapping.advanced.section
            .temporal.score_table.rows[2].functions,
          currentLanguagueReport?.health?.brain_mapping.advanced.section
            .temporal.score_table.rows[2].symptoms,
          [getNumber(agingRoiScore?.Middle_Temporal_LH)],
          [getNumber(agingRoiScore?.Middle_Temporal_RH)],
        ],
        background: 'White',
      },
      {
        data: [
          {
            header: [
              {
                // key: 'T4.',
                // content: 'Inferior Temporal',
                // note: t('IInferiorT4'),
                key: currentLanguagueReport?.health?.brain_mapping.advanced
                  .section.temporal.score_table.rows[3].rois[0].code,
                content:
                  currentLanguagueReport?.health?.brain_mapping.advanced.section
                    .temporal.score_table.rows[3].rois[0].name,
                note: currentLanguagueReport?.health?.brain_mapping.advanced
                  .section.temporal.score_table.rows[3].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainT4.png'),
          },
          currentLanguagueReport?.health?.brain_mapping.advanced.section
            .temporal.score_table.rows[3].functions,
          currentLanguagueReport?.health?.brain_mapping.advanced.section
            .temporal.score_table.rows[3].symptoms,
          [getNumber(agingRoiScore?.Inferior_Temporal_LH)],
          [getNumber(agingRoiScore?.Inferior_Temporal_RH)],
        ],
        background: 'White',
      },
      {
        data: [
          {
            header: [
              {
                // key: 'T5.',
                // content: 'Transverse Temporal',
                // note: t('ILateralT5'),
                key: currentLanguagueReport?.health?.brain_mapping.advanced
                  .section.temporal.score_table.rows[4].rois[0].code,
                content:
                  currentLanguagueReport?.health?.brain_mapping.advanced.section
                    .temporal.score_table.rows[4].rois[0].name,
                note: currentLanguagueReport?.health?.brain_mapping.advanced
                  .section.temporal.score_table.rows[4].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainT5.png'),
          },
          currentLanguagueReport?.health?.brain_mapping.advanced.section
            .temporal.score_table.rows[4].functions,
          currentLanguagueReport?.health?.brain_mapping.advanced.section
            .temporal.score_table.rows[4].symptoms,
          [getNumber(agingRoiScore?.Transverse_Temporal_LH)],
          [getNumber(agingRoiScore?.Transverse_Temporal_RH)],
        ],
        background: 'White',
      },
    ],
    [isEn, currentLanguagueReport],
  )

  return (
    <div className={classes.brainFunctionMapping}>
      <PageWrap>
        <PageContent>
          <WrapElement
            number={2}
            text={
              currentLanguagueReport?.health?.brain_mapping.advanced.section
                .temporal.title || ''
            }
          >
            <CardContent>
              <FlexBox>
                <div className={classes.contentDetailedAnalysis}>
                  <div className={classes.textHeader}>
                    <span>
                      {
                        currentLanguagueReport?.health?.brain_mapping.advanced
                          .section.temporal.description[0]
                      }
                    </span>
                    &nbsp;
                    <span className={classes.headerColorGreen}>
                      {
                        currentLanguagueReport?.health?.brain_mapping.advanced
                          .section.temporal.description[1]
                      }
                    </span>
                    &nbsp;
                    <span>
                      {
                        currentLanguagueReport?.health?.brain_mapping.advanced
                          .section.temporal.description[2]
                      }
                    </span>
                  </div>
                  <div className={classes.textContent}>
                    <p>
                      {
                        currentLanguagueReport?.health?.brain_mapping.advanced
                          .section.temporal.description[3]
                      }
                    </p>
                    {/* <p>{t('ITemporalLobeDisorders')}</p>
                    <p>{t('IDetailedAnalysis2')}</p> */}
                  </div>
                </div>
                <div className={classes.imgDetailedAnalysis}>
                  <div className={classes.scoreSpace}>
                    <div className={classes.scoreContainer}>
                      <div>
                        <div className={classes.headerScore}>
                          {
                            currentLanguagueReport?.health?.brain_mapping
                              .advanced.section.temporal.score_by_side.left
                          }
                        </div>
                        <Score score={getNumber(Frontal_LH)} />
                      </div>
                    </div>
                    <div className={classes.scoreContainer}>
                      <div>
                        <div className={classes.headerScore}>
                          {
                            currentLanguagueReport?.health?.brain_mapping
                              .advanced.section.temporal.score_by_side.right
                          }
                        </div>
                        <Score score={getNumber(Temporal_LH)} />
                      </div>
                    </div>
                  </div>
                  <div className={classes.imageOverviewBrain}>
                    <img
                      src={getHealthReportReportImage('brainPage2.png')}
                      alt=''
                    />
                  </div>
                </div>
              </FlexBox>
            </CardContent>
          </WrapElement>

          <div className={classes.marginBottom60}>
            <TableFiguresByPart
              head={headTable}
              body={bodyTable3 as any}
              isLocalLanguage={false}
            />
            <div className={classes.textBottomTable}>
              {
                currentLanguagueReport?.health?.brain_mapping.basic.section
                  .lobes.score_table.annotation
              }
            </div>
          </div>
        </PageContent>
      </PageWrap>
    </div>
  )
}

export default TablePrintT1
